// Chop etilgan kartani (vaucherni) ishlatish — BITTA endpoint bilan
// kontrakt: `POST /api/vouchers/activate`
// (`VoucherController.activate`, `:329-337`).
//
// =====================================================================
// SO'ROV VA JAVOB — O'QIB OLINGAN, TAXMIN QILINMAGAN
// =====================================================================
//   so'rov  : { code, login }        ← `ActivationRequest`, IKKALASI HAM
//                                      `@NotBlank`
//   javob   : `Voucher` (envelope ochilgandan keyin) — kod, holat,
//             abonent va grant identifikatorlari, `expiresAt`
//
// ⚠️ KARTA — ABONENT EMAS (`Voucher` javadoc'i). `login` mavjud
// abonentga BOG'LANADI, topilmasa YANGI abonent OCHILADI
// (`VoucherServiceImpl.findOrCreate`). Ya'ni kodning o'zi yetmaydi va
// «kimga» degan savol majburiy — ekran ham shuni so'raydi.
//
// ⛔ `Idempotency-Key` YUBORILMAYDI VA BU O'LCHANGAN QAROR.
// Kontroller metodi sarlavhani umuman qabul qilmaydi (imzosida
// `@RequestHeader` yo'q), servis esa grantni KALITSIZ ikki argumentli
// `grants.issue(request, actor)` bilan beradi. Yuborilgan kalit
// jimgina tashlanardi. Takrorni to'sadigan narsa boshqa: kartaning
// kodi. Batafsil dalil — `activationAttempt.ts` fayl boshida.
//
// ⚠️ `features/*` bir-birini import qilmaydi (`07-FRONTEND.md` §6):
// `LOGIN_PATTERN` shu yerda QAYTA e'lon qilingan, `features/subscriber`
// dan olinmagan.

import axios from 'axios'
import { z } from 'zod'
import { api } from '@/shared/api/client'
import { uuidSchema } from '@/shared/api/schemas'

/**
 * Login naqshi — `ActivationRequest` dagi `@Pattern` ning AYNAN o'zi.
 *
 * ⚠️ TAKROR ATAYLAB (`features/subscriber/subscriberApi.ts` dagi
 * `LOGIN_PATTERN` bilan bir xil sabab): server rad javobi bannerda,
 * maydonning yonida emas. Bu yerdagi tekshiruv esa yozilayotgan
 * paytda ko'rinadi.
 *
 * ⚠️ Naqsh `SubscriberDraft#login` bilan BIR XIL bo'lishi SHART va bu
 * backend javadoc'ida ochiq yozilgan: login mavjud bo'lmasa draft
 * KODDA yig'iladi, ya'ni bean-validation u yerda yurmaydi va noto'g'ri
 * login bazadagi `subscriber_login_shape` cheklovigacha yetib boradi.
 */
export const LOGIN_PATTERN = /^[A-Za-z0-9._@-]{1,64}$/

/**
 * Kodni server bilan BIR XIL normallashtiradi —
 * `VoucherServiceImpl.normalize`: `trim` + katta harf + bo'shliqlarni
 * olib tashlash.
 *
 * ⚠️ CHIZIQCHA (`-`) OLIB TASHLANMAYDI. Server ham uni saqlaydi, va
 * partiya prefiksi (`VoucherCodeGenerator#generate` dagi `prefix`)
 * ixtiyoriy satr — tenant unga chiziqcha qo'ygan bo'lishi mumkin.
 * Panel serverdan qattiqroq tozalasa, haqiqiy kod topilmay qolardi.
 *
 * ⚠️ `toUpperCase`, `toLocaleUpperCase` EMAS: server `Locale.ROOT`
 * ishlatadi va turkcha `i` da ikkalasi ajraladi.
 */
export function normalizeVoucherCode(raw: string): string {
  return raw.trim().toUpperCase().replaceAll(' ', '')
}

/**
 * ⚠️ FAQAT ISHLATILADIGAN MAYDONLAR. `Voucher` da o'n bitta maydon
 * bor; ularning hammasini sxemaga yozish backend bittasini
 * o'zgartirgan kuni ekranni yiqitardi, hech qanday foyda bermasdan.
 * Zod ortiqcha maydonlarni jimgina tashlaydi.
 */
const activatedSchema = z.object({
  id: uuidSchema,
  code: z.string(),
  /**
   * Karta AKTIVLASHTIRILGANDAN KEYIN qancha amal qiladi
   * (`Voucher#expiresAt` javadoc'i) — partiyaning `validUntil` i EMAS.
   *
   * ⚠️ `null` BO'LISHI MUMKIN va bu «muddatsiz» degani EMAS: plan
   * `validityDays` bermasa server bu maydonni umuman hisoblamaydi
   * (`plan.validityDays() == null ? null : ...`). Ekran o'sha holatda
   * HECH NARSA aytmaydi — «cheksiz» deb yozish o'ylab topilgan
   * ma'lumot bo'lardi.
   *
   * ⚠️ `.nullish()`: Jackson `non_null` bilan sozlangan, ya'ni bo'sh
   * maydon `null` bo'lib emas, UMUMAN kelmasdan qoladi.
   */
  expiresAt: z.string().nullish(),
})

export interface ActivatedVoucher {
  id: string
  code: string
  /** `null` — plan muddat bermagan; «cheksiz» DEGANI EMAS */
  expiresAt: string | null
}

/**
 * Faollashtirish natijasi — SAKKIZ HOLAT, «bo'ldi / bo'lmadi» EMAS.
 *
 * =====================================================================
 * ⚠️ NEGA BITTA «xato» GA YIG'ILMAYDI
 * =====================================================================
 * Kassir mijoz qarshisida turibdi va HAR holatda BOSHQA ish qiladi.
 * Ikkalasi bitta qizil banner bo'lsa, u eng ko'p uchraydigan javobni
 * («qayta urinib ko'ring») hamma holatga qo'llaydi — va ularning
 * yarmida bu noto'g'ri.
 *
 * ⛔ ENG MUHIM AJRATMA — KARTA KUYDIMI:
 *
 *   kuydi           : `ACTIVATED`, `USED`, `OWN_RETRY`
 *   umrbod yaroqsiz : `EXPIRED`, `VOIDED`
 *   TEGILMADI       : `NOT_FOUND`, `SUBSCRIBER_MISSING`, `LOGIN_CLOSED`,
 *                     `PLAN_UNAVAILABLE`
 *
 * Oxirgi uchtasi o'lchangan: `activate` `@Transactional` va ikkala
 * istisno ham shartli `UPDATE` dan OLDIN tashlanadi
 * (`findOrCreate` → `IllegalTransition`, `catalog.findPlan` →
 * `PlanUnavailable`), ya'ni tranzaksiya qaytariladi va kod bazada
 * o'zgarmagan holicha qoladi. Kassirga «karta yaroqli» deb aytish —
 * mijozning pulini saqlash: aks holda u yaroqli kartani chiqarib
 * tashlardi.
 */
export type ActivationOutcome =
  /** Bajarildi: karta kuydi, abonentga paket berildi */
  | { kind: 'ACTIVATED'; voucher: ActivatedVoucher }
  /**
   * Karta allaqachon ishlatilgan (409 `VOUCHER_ACTIVATED`).
   *
   * ⚠️ Bu HOLAT, texnik xato emas: qayta urinish AYNI javobni beradi.
   */
  | { kind: 'USED' }
  /** Partiyaning `validUntil` i o'tgan (409 `VOUCHER_EXPIRED`) */
  | { kind: 'EXPIRED' }
  /** Partiya bekor qilingan: buzilgan, yo'qolgan, o'g'irlangan (409 `VOUCHER_VOIDED`) */
  | { kind: 'VOIDED' }
  /** Bunday kod yo'q (404) — kartaga TEGILMADI */
  | { kind: 'NOT_FOUND' }
  /** Login topilmadi — karta TEGILMADI, uni qayta ishlatish mumkin */
  | { kind: 'SUBSCRIBER_MISSING' }
  /**
   * Login YOPILGAN abonentga tegishli (409 `ILLEGAL_TRANSITION`).
   *
   * ⚠️ `findOrCreate` faqat `CLOSED` da tashlaydi (`isClosed()` —
   * terminal holat). `SUSPENDED` va `BLOCKED` abonentga karta
   * ishlaydi, ya'ni bu yerda «bloklangan» deb yozish noto'g'ri
   * bo'lardi.
   */
  | { kind: 'LOGIN_CLOSED'; serverText: string | null }
  /**
   * Kartaning plani katalogda yo'q (422 `UNPROCESSABLE_ENTITY` —
   * `PlanUnavailable`). Mijozning ham, kassirning ham aybi emas.
   */
  | { kind: 'PLAN_UNAVAILABLE'; serverText: string | null }

/** Panel qo'shadigan yagona holat — `activationAttempt.ts` ga qarang. */
export type ActivationVerdict = ActivationOutcome | { kind: 'OWN_RETRY' }

interface Refusal {
  status: number
  tag: string | null
  text: string | null
}

/**
 * Rad javobining ikki barqaror belgisi: HTTP kodi va `tag`.
 *
 * ⚠️ MATN BO'YICHA SHART YOZILMAYDI — `tag` backendning shartnomasi
 * (`ErrorData` javadoc'i: «matn — odam uchun, tag — shartnoma»), matn
 * esa istalgan kuni o'zgaradi.
 *
 * ⚠️ `instanceof XsibApiError` EMAS, `axios.isAxiosError`: envelope'ni
 * ochadigan interceptor FAQAT muvaffaqiyatli javobda ishlaydi
 * (`client.ts` da uning xato ishlovchisi yo'q), ya'ni 404/409/422
 * bu yergacha xom `AxiosError` bo'lib keladi. `cashdeskApi.ts` dagi
 * `insufficientFunds` bilan bir xil naqsh.
 */
function readRefusal(error: unknown): Refusal | null {
  if (!axios.isAxiosError(error) || !error.response) return null
  const body: unknown = error.response.data
  let tag: string | null = null
  let text: string | null = null
  if (body && typeof body === 'object' && 'error' in body) {
    const envelope = (body as { error?: { tag?: unknown; text?: unknown } | null }).error
    if (envelope && typeof envelope.tag === 'string') tag = envelope.tag
    if (envelope && typeof envelope.text === 'string') text = envelope.text
  }
  return { status: error.response.status, tag, text }
}

/**
 * Rad javobini holatga o'giradi; TANIMASA `null` — chaqiruvchi uni
 * yuqoriga tashlaydi va `translateError` umumiy qatlami ishlaydi.
 *
 * ⚠️ RO'YXAT YOPIQ VA BU ATAYLAB. Noma'lum `tag` ni «karta yaroqsiz»
 * deb ko'rsatish eng yomon variant bo'lardi: 500 yoki proksi xatosida
 * kassir yaroqli kartani chiqarib tashlardi.
 */
function toOutcome(refusal: Refusal): ActivationOutcome | null {
  /*
    ⛔ IKKALASI HAM 404 — va ularni AJRATMASLIK eng qimmat xato bo'lardi.

    `SUBSCRIBER_MISSING` — karta YAXSHI, abonent yo'q. `NOT_FOUND` esa
    kassirga «bunday karta yo'q» deydi va u yaroqli kartani chiqarib
    tashlardi. Server belgini shu sababdan ataylab ajratgan
    (`SubscriberExceptionHandler.voucherSubscriberMissing`).
  */
  if (refusal.status === 404 && refusal.tag === 'SUBSCRIBER_MISSING') {
    return { kind: 'SUBSCRIBER_MISSING' }
  }
  if (refusal.status === 404) return { kind: 'NOT_FOUND' }
  if (refusal.status === 409) {
    // `SubscriberExceptionHandler.voucherNotActivatable`:
    // tag = "VOUCHER_" + holat nomi (`VoucherState`).
    if (refusal.tag === 'VOUCHER_ACTIVATED') return { kind: 'USED' }
    if (refusal.tag === 'VOUCHER_EXPIRED') return { kind: 'EXPIRED' }
    if (refusal.tag === 'VOUCHER_VOIDED') return { kind: 'VOIDED' }
    if (refusal.tag === 'ILLEGAL_TRANSITION') {
      return { kind: 'LOGIN_CLOSED', serverText: refusal.text }
    }
    return null
  }
  if (refusal.status === 422 && refusal.tag === 'UNPROCESSABLE_ENTITY') {
    return { kind: 'PLAN_UNAVAILABLE', serverText: refusal.text }
  }
  return null
}

/**
 * Javob KELMAGANMI — ya'ni so'rov serverga yetgani NOMA'LUM.
 *
 * ⚠️ Timeout ham shu yerga tushadi (`API_TIMEOUT_MS`): axios unda
 * `response` bermaydi. Aynan shu holat eng xavflisi — so'rov yetib
 * borgan, javob yo'lda yo'qolgan bo'lishi mumkin.
 */
export function isUnanswered(error: unknown): boolean {
  return axios.isAxiosError(error) && !error.response
}

/**
 * Kartani faollashtiradi.
 *
 * ⚠️ TANIGAN rad javoblarini XATO SIFATIDA TASHLAMAYDI — ular biznes
 * holatlari va ekranda har biri boshqa ish aytadi. Tanimaganini esa
 * tashlaydi: «hammasini yutish» 500 ni ham «karta yaroqsiz» qilib
 * ko'rsatardi.
 */
export async function activateVoucher(
  code: string,
  login: string,
): Promise<ActivationOutcome> {
  try {
    const response = await api.post<unknown>('/api/vouchers/activate', {
      code: normalizeVoucherCode(code),
      login: login.trim(),
    })
    const parsed = activatedSchema.parse(response.data)
    return {
      kind: 'ACTIVATED',
      voucher: { id: parsed.id, code: parsed.code, expiresAt: parsed.expiresAt ?? null },
    }
  } catch (error) {
    const refusal = readRefusal(error)
    const outcome = refusal === null ? null : toOutcome(refusal)
    if (outcome === null) throw error
    return outcome
  }
}
